<template>
  <div
    class="region-selector"
    :class="{ 'is-open': isOpen }"
    @click="handleTriggerClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="region-trigger" :class="{ active: isOpen }">
      <span class="region-text">
        {{ currentRegion.country }} / {{ currentRegion.currencySymbol }} {{ currentRegion.currency }}
      </span>
      <i class="iconfont icon-xiangxiajiantou arrow-icon" :class="{ 'arrow-up': isOpen }"></i>
    </div>

    <transition name="region-fade">
      <div v-if="isOpen" class="region-dropdown-shell">
        <div class="region-dropdown" @click.stop @mouseenter="handleDropdownEnter" @mouseleave="handleDropdownLeave">
          <!-- Header -->
          <div class="region-dropdown__header">
            <div class="region-dropdown__current">
              <i class="iconfont icon-a-wanchengxiao check-icon"></i>
              <span class="region-dropdown__current-text">
                <span class="region-dropdown__current-country" :title="currentRegion.country">
                  {{ currentRegion.country }}
                </span>
                <span class="region-dropdown__current-detail">
                  - {{ currentRegion.language }} / {{ currentRegion.currencySymbol }} {{ currentRegion.currency }}
                </span>
              </span>
            </div>
            <i class="iconfont icon-a-Deleteshanchu close-icon" @click="handleClose"></i>
          </div>

          <div class="region-dropdown__divider"></div>

          <!-- Search -->
          <div class="region-dropdown__search">
            <FsInput
              v-model="searchValue"
              :placeholder="t('components/layout.regionSearch')"
              new-regular
              search
              clearable
              @clear="searchValue = ''"
            />
          </div>

          <!-- Region List -->
          <div class="region-dropdown__list">
            <div
              v-for="item in filteredRegions"
              :key="item.key"
              class="region-dropdown__item"
              :class="{ 'region-dropdown__item--active': item.key === currentRegion.key }"
              @click="handleSelect(item)"
            >
              <span class="region-dropdown__item-country">{{ item.displayCountry }} -</span>
              <span class="region-dropdown__item-detail">
                {{ item.displayLanguage }} / {{ item.currencySymbol }} {{ item.currency }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <div v-if="isOpen && isMobileViewport()" class="region-mobile-mask" @click.stop="handleClose"></div>
    <div
      v-if="isSwitching"
      v-loading="isSwitching"
      class="region-switch-loading"
      role="status"
      aria-live="polite"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useLocaleStore } from '@/store/locale'
import { useWebsiteInfoStore } from '@/store/websiteInfo'
import { useI18n } from '@/hooks/useI18n'
import { isValidSite } from '@/constant/siteMapping'
import { getSiteRelation, updateSiteInfo, type SiteRelationItem, type UpdateSiteInfoData } from '@/api/modules/common'
import { Cookie } from '@/utils'
import { getWebsiteRouteSite } from '@/utils/modules/site'
import { replaceRouteSite } from '@/router'

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'open-change', value: boolean): void
}>()

interface RegionItem {
  key: string
  country: string
  language: string
  currency: string
  currencySymbol: string
  displayCountry: string
  displayLanguage: string
  site?: string
  source: SiteRelationItem
}

type WebsiteInfoCookieSource = Omit<
  Partial<SiteRelationItem & UpdateSiteInfoData>,
  'id' | 'countries_id' | 'language_id'
> & {
  id?: number | string | null
  countries_id?: number | string | null
  language_id?: number | string | null
}

const localeStore = useLocaleStore()
const websiteInfoStore = useWebsiteInfoStore()
const route = useRoute()
const WEBSITE_INFO_COOKIE = 'fs_websiteinfo'
const WEBSITE_INFO_MAX_AGE = 60 * 60 * 24 * 30
const MAIN_SITE_ISO_CODE = 'US'
// 临时过滤中国大陆站点
const TEMP_HIDDEN_REGION_ID = 328

const createRegionKey = (item: SiteRelationItem) =>
  [item.id, item.countries_id, item.language, item.currency, item.iso_code].filter(Boolean).join('-')

const pickString = (...values: unknown[]) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value
    }
  }
  return ''
}

const toNumber = (value: unknown, fallback: number) => {
  if (value === undefined || value === null || value === '') return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const createRegionSource = (source: WebsiteInfoCookieSource): SiteRelationItem | null => {
  const country = pickString(
    source.countries_name,
    source.country_name,
    source.en_countries_name,
    websiteInfoStore.countries_name,
    websiteInfoStore.country_name,
    websiteInfoStore.en_countries_name
  )
  const language = pickString(source.language, websiteInfoStore.language)
  const currency = pickString(source.currency, websiteInfoStore.currency)
  const isoCode = pickString(source.iso_code, websiteInfoStore.iso_code)
  if (!country || !language || !currency || !isoCode) return null

  return {
    id: toNumber(source.id, websiteInfoStore.id),
    countries_id: toNumber(source.countries_id, websiteInfoStore.countries_id),
    countries_name: country,
    country_name: pickString(source.country_name, country),
    countries_chinese_name: source.countries_chinese_name,
    en_countries_name: pickString(source.en_countries_name, country),
    iso_code: isoCode,
    language,
    languageOriginalForRequest: pickString(source.languageOriginalForRequest, language),
    language_id: toNumber(source.language_id, websiteInfoStore.language_id || 0),
    currency,
    symbol: pickString(source.symbol, websiteInfoStore.symbol),
    tel_prefix: source.tel_prefix,
    website: source.website,
    locale: source.locale,
    warehouse: source.warehouse,
    isEuUnion: source.isEuUnion,
    timezone: source.timezone,
    seo_lang_code: source.seo_lang_code,
    citys: source.citys,
  }
}

const createFallbackSource = (): SiteRelationItem => createRegionSource(websiteInfoStore.$state) as SiteRelationItem

const mapRegionItem = (item: SiteRelationItem): RegionItem | null => {
  const country = item.countries_name || item.country_name || item.en_countries_name
  if (!country || !item.language || !item.currency) return null

  return {
    key: createRegionKey(item),
    country,
    language: item.language,
    currency: item.currency,
    currencySymbol: item.symbol || '',
    displayCountry: item.en_countries_name || country,
    displayLanguage: item.languageOriginalForRequest || item.language,
    site: item.website || item.locale,
    source: item,
  }
}

const parseWebsiteInfoCookie = (value: string): WebsiteInfoCookieSource | null => {
  try {
    return JSON.parse(value) as WebsiteInfoCookieSource
  } catch {
    try {
      return JSON.parse(decodeURIComponent(value)) as WebsiteInfoCookieSource
    } catch (decodeError) {
      console.error('fs_websiteinfo JSON parse error:', decodeError)
      return null
    }
  }
}

const getStoredSite = () => {
  try {
    return localStorage.getItem('design_center_site')
  } catch {
    return null
  }
}

const getWebsiteInfoCookieValues = () => {
  if (typeof document === 'undefined') return []
  const cookieName = `${encodeURIComponent(WEBSITE_INFO_COOKIE)}=`
  const cookieValues = document.cookie
    .split(/;\s*/)
    .filter(cookie => cookie.startsWith(cookieName))
    .map(cookie => decodeURIComponent(cookie.slice(cookieName.length)))
  const fallbackCookie = Cookie.get(WEBSITE_INFO_COOKIE)

  return cookieValues.length ? cookieValues : fallbackCookie ? [fallbackCookie] : []
}

const getWebsiteInfoFromCookie = () => {
  const websiteInfos = getWebsiteInfoCookieValues()
    .map(parseWebsiteInfoCookie)
    .filter((item): item is WebsiteInfoCookieSource => Boolean(item))
  if (!websiteInfos.length) return null

  const currentSite = getStoredSite() || localeStore.currentSite
  const matchedWebsiteInfo = currentSite
    ? websiteInfos.find(item => item.website === currentSite || item.locale === currentSite)
    : null

  return matchedWebsiteInfo || websiteInfos[websiteInfos.length - 1]
}

const createRegionFromWebsiteInfo = (websiteInfo: WebsiteInfoCookieSource | null) => {
  if (!websiteInfo) return null
  const source = createRegionSource(websiteInfo)
  return source ? mapRegionItem(source) : null
}

const getCurrentRegionFromCookie = () => createRegionFromWebsiteInfo(getWebsiteInfoFromCookie())

const regionList = ref<RegionItem[]>([])
const currentRegion = ref<RegionItem>(
  getCurrentRegionFromCookie() || (mapRegionItem(createFallbackSource()) as RegionItem)
)
const searchValue = ref('')
const isOpen = ref(false)
const isSwitching = ref(false)
let selectRequestId = 0

// 延迟关闭定时器
let hideTimer: ReturnType<typeof setTimeout> | null = null
const HIDE_DELAY = 200

const clearHideTimer = () => {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

const scheduleHide = () => {
  clearHideTimer()
  hideTimer = setTimeout(() => {
    isOpen.value = false
    searchValue.value = ''
  }, HIDE_DELAY)
}

const handleMouseEnter = () => {
  if (isMobileViewport()) return
  clearHideTimer()
  isOpen.value = true
}

const handleMouseLeave = () => {
  if (isMobileViewport()) return
  scheduleHide()
}

const isMobileViewport = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches

/** 移动端点击后从上往下滑出面板，桌面端仍由鼠标悬停控制下拉框。 */
const handleTriggerClick = (event: MouseEvent) => {
  if (!isMobileViewport()) return
  event.stopPropagation()
  clearHideTimer()
  isOpen.value = !isOpen.value
  if (!isOpen.value) searchValue.value = ''
}

const handleDropdownEnter = () => {
  clearHideTimer()
}

const handleDropdownLeave = () => {
  scheduleHide()
}

const filteredRegions = computed(() => {
  const visibleRegions = regionList.value.filter(item => Number(item.source.id) !== TEMP_HIDDEN_REGION_ID)
  if (!searchValue.value) return visibleRegions

  const keyword = searchValue.value.toLowerCase()
  return visibleRegions.filter(
    (item: RegionItem) =>
      item.country.toLowerCase().includes(keyword) ||
      item.language.toLowerCase().includes(keyword) ||
      item.displayCountry.toLowerCase().includes(keyword) ||
      item.displayLanguage.toLowerCase().includes(keyword) ||
      item.currency.toLowerCase().includes(keyword) ||
      item.currencySymbol.toLowerCase().includes(keyword) ||
      (item.source.iso_code || '').toLowerCase().includes(keyword)
  )
})

const isSameWebsiteInfoRegion = (item: RegionItem, websiteInfo: SiteRelationItem) => {
  const source = item.source
  const isSameCountryAndCurrency = source.iso_code === websiteInfo.iso_code && item.currency === websiteInfo.currency
  if (!isSameCountryAndCurrency) return false

  return (
    source.language === websiteInfo.language ||
    source.languageOriginalForRequest === websiteInfo.language ||
    item.language === websiteInfo.language
  )
}

const findCurrentRegion = (regions: RegionItem[], source?: WebsiteInfoCookieSource | null) => {
  const websiteInfo = createRegionSource(source || getWebsiteInfoFromCookie() || websiteInfoStore.$state)
  if (!websiteInfo) return null

  return (
    regions.find(item => item.source.id === websiteInfo.id) ||
    regions.find(item => isSameWebsiteInfoRegion(item, websiteInfo)) ||
    regions.find(item => item.source.iso_code === websiteInfo.iso_code && item.currency === websiteInfo.currency) ||
    regions.find(item => item.site === localeStore.currentSite || item.site === websiteInfo.website)
  )
}

const setWebsiteInfoCookie = (data: unknown) => {
  const value = JSON.stringify(data)
  Cookie.set(WEBSITE_INFO_COOKIE, '', { maxAge: -1, domain: '' })
  Cookie.set(WEBSITE_INFO_COOKIE, '', { maxAge: -1 })
  Cookie.set(WEBSITE_INFO_COOKIE, value, { maxAge: WEBSITE_INFO_MAX_AGE, domain: '' })
  Cookie.set(WEBSITE_INFO_COOKIE, value, { maxAge: WEBSITE_INFO_MAX_AGE })
}

const normalizeWebsiteSegment = (website: string) => {
  const segment = website
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '')
  if (!/^[a-z]{2}(?:-[a-z]{2})?$/.test(segment)) return ''

  return getWebsiteRouteSite(segment)
}

const updateSiteUrl = async (websiteInfo: UpdateSiteInfoData) => {
  const isMainSite = websiteInfo.iso_code.toUpperCase() === MAIN_SITE_ISO_CODE
  const website = normalizeWebsiteSegment(websiteInfo.website)
  if (!isMainSite && !website) return false

  const nextSite = isMainSite ? '' : website
  const currentSite = typeof route.params.site === 'string' ? route.params.site : ''
  if (nextSite === currentSite) return false

  await replaceRouteSite(nextSite)
  return true
}

const fetchRegions = async () => {
  const { data, code } = await getSiteRelation()
  if (code !== 200 || !Array.isArray(data?.list)) return []

  return data.list.map(mapRegionItem).filter((item): item is RegionItem => Boolean(item))
}

const applyRegions = (regions: RegionItem[], source?: WebsiteInfoCookieSource | null) => {
  if (!regions.length) return null

  regionList.value = regions

  currentRegion.value =
    findCurrentRegion(regions, source || getWebsiteInfoFromCookie()) ||
    (source ? createRegionFromWebsiteInfo(source) : getCurrentRegionFromCookie()) ||
    regions[0]

  return currentRegion.value
}

const loadRegions = async (source?: WebsiteInfoCookieSource | null) => {
  try {
    return applyRegions(await fetchRegions(), source)
  } catch (error) {
    console.error('获取站点关系失败:', error)
    return null
  }
}

const refreshRegionsAfterSelect = async (requestId: number, source: WebsiteInfoCookieSource) => {
  try {
    const regions = await fetchRegions()
    if (requestId !== selectRequestId) return null

    return applyRegions(regions, source)
  } catch (error) {
    if (requestId === selectRequestId) {
      console.error('刷新站点关系失败:', error)
    }
    return null
  }
}

const handleSelect = async (item: RegionItem) => {
  if (isSwitching.value) return
  isSwitching.value = true
  const requestId = ++selectRequestId
  // 选中后立即收起面板，不等待站点信息、语言和路由异步更新完成。
  searchValue.value = ''
  isOpen.value = false

  if (item.key === currentRegion.value.key) {
    const currentSite = item.site?.toLowerCase()
    if (
      currentSite &&
      isValidSite(currentSite) &&
      (getStoredSite() !== currentSite || localeStore.currentSite !== currentSite)
    ) {
      try {
        await localeStore.setLocale(currentSite)
      } catch (error) {
        isSwitching.value = false
        console.error('更新语言失败:', error)
        return
      }
      if (requestId !== selectRequestId) {
        isSwitching.value = false
        return
      }
      localeStore.notifySiteChange()
    }
    isSwitching.value = false
    return
  }
  try {
    const { data, code } = await updateSiteInfo({
      iso_code: item.source.iso_code,
      language: item.source.languageOriginalForRequest || item.source.language,
      currency: item.source.currency,
    })
    if (requestId !== selectRequestId) {
      isSwitching.value = false
      return
    }
    if (code !== 200 || !data) {
      isSwitching.value = false
      return
    }

    websiteInfoStore.updateWebsiteInfo(data)
    setWebsiteInfoCookie(data)

    await updateSiteUrl(data)

    const site = (data.website || data.locale)?.toLowerCase()
    const localeUpdatePromise = site && isValidSite(site) ? localeStore.setLocale(site) : null
    const refreshedRegionPromise = refreshRegionsAfterSelect(requestId, data)

    if (localeUpdatePromise) {
      await localeUpdatePromise
    }
    if (requestId !== selectRequestId) {
      isSwitching.value = false
      return
    }
    localeStore.notifySiteChange()

    const refreshedRegion = await refreshedRegionPromise
    if (requestId !== selectRequestId) {
      isSwitching.value = false
      return
    }

    currentRegion.value =
      refreshedRegion || findCurrentRegion(regionList.value, data) || createRegionFromWebsiteInfo(data) || item
    isSwitching.value = false
  } catch (error) {
    isSwitching.value = false
    if (requestId === selectRequestId) {
      console.error('更新站点信息失败:', error)
    }
  }
}

const handleClose = () => {
  searchValue.value = ''
  isOpen.value = false
}

const REGION_OVERLAY_SOURCE = 'region'
const CLOSE_MOBILE_OVERLAYS_EVENT = 'close-mobile-overlays'

/** 账户抽屉或手册菜单打开时，收起语言下拉。 */
const handleOverlayClose = (event: Event) => {
  const source = (event as CustomEvent<{ source?: string }>).detail?.source
  if (source !== REGION_OVERLAY_SOURCE && isOpen.value) handleClose()
}

onMounted(() => {
  loadRegions()
  document.addEventListener(CLOSE_MOBILE_OVERLAYS_EVENT, handleOverlayClose)
})

watch(isOpen, value => {
  emit('open-change', value)
  if (value && isMobileViewport()) {
    document.dispatchEvent(new CustomEvent(CLOSE_MOBILE_OVERLAYS_EVENT, { detail: { source: REGION_OVERLAY_SOURCE } }))
  }
})

onBeforeUnmount(() => {
  clearHideTimer()
  document.removeEventListener(CLOSE_MOBILE_OVERLAYS_EVENT, handleOverlayClose)
})
</script>

<style lang="scss" scoped>
.region-selector {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;

  &.is-open {
    z-index: 2001;
  }
}

.region-trigger {
  display: flex;
  align-items: center;
  gap: 4px;
  height: auto;
  padding: 0 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    .region-text {
      color: #19191a;
    }
  }

  .region-text {
    @include font13;
    color: #707070;
    white-space: nowrap;
    transition: color 0.2s;
  }

  .arrow-icon {
    font-size: 10px;
    color: #19191a;
    transition: transform 0.2s;

    &.arrow-up {
      transform: rotate(180deg);
    }
  }
}

.region-dropdown-shell {
  position: absolute;
  top: calc(100% + 15px);
  right: 0;
  z-index: 2002;
}

.region-dropdown {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 300px;
  height: 440px;
  box-sizing: border-box;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f4f5f6;

  // 顶部三角形箭头
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    right: 68px;
    width: 18px;
    height: 18px;
    background: #fff;
    border-top: 1px solid #f4f5f6;
    border-left: 1px solid #f4f5f6;
    transform: rotate(45deg);
  }

  &__header {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 16px 20px 12px;
  }

  &__current {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 8px;
    min-width: 0;

    .check-icon {
      flex-shrink: 0;
      font-size: 16px;
      color: #10a300;
    }
  }

  &__current-text {
    @include font14;
    display: flex;
    min-width: 0;
    font-weight: 600;
    color: #19191a;
  }

  &__current-country {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__current-detail {
    flex-shrink: 0;
    white-space: nowrap;
  }

  .close-icon {
    flex-shrink: 0;
    font-size: 16px;
    color: #707070;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: background 0.2s;

    &:hover {
      background: rgba(25, 25, 26, 0.06);
      color: #19191a;
    }
  }

  &__divider {
    flex-shrink: 0;
    height: 1px;
    background: #eeeeee;
    margin: 0 20px;
  }

  &__search {
    flex-shrink: 0;
    padding: 12px 20px;

    :deep(.fs-input) {
      width: 260px;
      height: 34px;
    }

    :deep(.fs-input__wrapper) {
      height: 34px;
      min-height: 34px;
    }

    :deep(.fs-input__inner) {
      height: 32px;
      line-height: 32px;
    }
  }

  &__list {
    flex: none;
    height: 311px;
    min-height: 0;
    margin: 0 0 0 20px;
    overflow-y: auto;
    padding: 0 20px 0 0;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 2px;

      &:hover {
        background: rgba(0, 0, 0, 0.2);
      }
    }
  }

  &__item {
    display: block;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    line-height: 18px;
    overflow-wrap: anywhere;
    transition: color 0.15s;

    &:hover {
      background: transparent;

      .region-dropdown__item-country,
      .region-dropdown__item-detail {
        color: #0060bf;
        text-decoration-line: underline;
        text-decoration-color: #0060bf;
        text-underline-offset: 2px;
      }
    }

    &--active {
      background: transparent;
    }
  }

  &__item-country {
    font-size: 12px;
    line-height: 18px;
    color: #19191a;
  }

  &__item-detail {
    font-size: 12px;
    line-height: 18px;
    color: #0060bf;
  }
}

// 过渡动画
.region-fade-enter-active,
.region-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.region-fade-enter-from,
.region-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media screen and (max-width: 768px) {
  .region-mobile-mask {
    position: fixed;
    z-index: 2001;
    inset: 50px 0 0;
    background: rgba(0, 0, 0, 0.35);
  }

  // 下拉层裁剪在导航栏下方，面板从上往下滑出，避免盖住顶栏。
  .region-dropdown-shell {
    position: fixed;
    top: 50px;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
    pointer-events: none;
  }

  .region-fade-enter-active,
  .region-fade-leave-active {
    transition: none;
  }

  .region-fade-enter-from,
  .region-fade-leave-to {
    opacity: 1;
    transform: none;
  }

  .region-fade-enter-active .region-dropdown,
  .region-fade-leave-active .region-dropdown {
    transition: transform 0.25s ease;
  }

  .region-fade-enter-from .region-dropdown,
  .region-fade-leave-to .region-dropdown {
    transform: translateY(-100%);
  }

  .region-dropdown {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 0;
    box-shadow: none;
    pointer-events: auto;
    padding: 0;

    .region-dropdown__header {
      padding: 16px;

      .region-dropdown__current-text {
        font-size: 14px;
        line-height: 22px;
        font-weight: 600;
        color: #19191a;
      }
    }

    .region-dropdown__divider {
      margin: 0 16px;
    }

    .region-dropdown__search {
      padding: 0 16px;
      margin: 12px 0;

      :deep(.fs-input),
      :deep(.fs-input__wrapper) {
        width: 100%;
        height: 34px;
        min-height: 34px;
      }

      :deep(.fs-input__inner) {
        height: 32px;
        line-height: 32px;
        font-size: 12px;
      }
    }

    .region-dropdown__list {
      height: auto;
      flex: 1;
      margin: 0;
      padding: 0 16px 16px;
    }

    .region-dropdown__item {
      padding: 12px;
      font-size: 14px;
      line-height: 22px;

      .region-dropdown__item-country,
      .region-dropdown__item-detail {
        font-size: 14px;
        line-height: 22px;
        color: #0060bf;
      }
    }

    &::before {
      display: none;
    }
  }

  .region-switch-loading {
    position: fixed;
    z-index: 2100;
    inset: 50px 0 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.72);
  }
}
</style>
